/**
 * useConversationsV2 - Hook for managing conversation list
 *
 * Provides conversation loading, selection, and deletion with
 * automatic refresh on context change.
 */

import { useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { useChatV2Store } from './useChatV2Store'
import type { Conversation } from '../types'

interface UseConversationsV2Options {
  contextId: string
  autoLoad?: boolean
}

export function useConversationsV2({
  contextId,
  autoLoad = true,
}: UseConversationsV2Options) {
  const {
    conversations,
    currentConversationId,
    isLoadingConversations,
    setCurrentConversationId,
    loadConversations,
    deleteConversation,
  } = useChatV2Store()

  // Load conversations on mount or context change
  useEffect(() => {
    if (autoLoad && contextId) {
      loadConversations(contextId)
    }
  }, [contextId, autoLoad, loadConversations])

  // Select a conversation
  const selectConversation = useCallback(
    (conversation: Conversation) => {
      setCurrentConversationId(conversation.id)
    },
    [setCurrentConversationId]
  )

  // Start a new chat
  const startNewChat = useCallback(() => {
    setCurrentConversationId(null)
  }, [setCurrentConversationId])

  // Delete and handle selection
  const handleDelete = useCallback(
    async (conversationId: string) => {
      try {
        await deleteConversation(conversationId)
        toast.success('Conversation deleted')
      } catch (error) {
        toast.error('Failed to delete conversation')
        throw error
      }
    },
    [deleteConversation]
  )

  // Refresh conversations
  const refresh = useCallback(() => {
    if (contextId) {
      loadConversations(contextId)
    }
  }, [contextId, loadConversations])

  // Get current conversation object
  const currentConversation = conversations.find(
    (c) => c.id === currentConversationId
  )

  return {
    conversations,
    currentConversation,
    currentConversationId,
    isLoading: isLoadingConversations,

    // Actions
    selectConversation,
    startNewChat,
    deleteConversation: handleDelete,
    refresh,
  }
}

export default useConversationsV2
